// Old URLs keep working (decision 2026-10-19): the Scorecard mode became Cadence, the DE screens
// moved to Agents and data entry moved from Setup to Cadence; then (KPIS-MODE, founder-approved
// 2026-10-22) Chart and Cadence became KPIs and Finance moved to Reports. Each old path
// redirects (replace, query kept) to its new home, so bookmarks, report mails and shared links
// still open.
//   /scorecard                → /kpis/scorecard     (`?view=` kept)
//   /scorecard/publications   → /kpis/publications
//   /scorecard/de/{id}        → /agents/de/{id}?tab=scorecard  (UX-B: the DE's one page)
//   /scorecard/de/{id}?tab=x  → /agents/de/{id}?tab=x
//   /setup/des                → /agents
//   /setup/des/{id}[?tab=x]   → same rule as /scorecard/de/{id}
//   /setup/entry/hours|numbers, /setup/import → /kpis/entry/hours|numbers, /kpis/import
//   /chart[/seat/{id}]        → /kpis/chart[/seat/{id}]   (/chart/editor → /kpis/chart)
//   /chart/finance[/x]        → /kpis/reports/finance[/x]
// NAV-REDESIGN (founder-approved 2026-10-29):
//   /reports[/x]              → /kpis/reports[/x]   (weeks, a report id, finance/…, live, unsubscribe)
//   /screens                  → /live               (`?lease=` kept)
//   /cadence                  → /kpis/scorecard
// SHELL-DECISIONS (2026-10-30): Home became Desk.
//   /home[/x]                 → /desk[/x]          (query and hash kept)
//   /work/home, /work/desk    → /work/boards       (c.$slug.work.tsx; Work's overview is Boards)
//   /cadence/meeting[/{id}]   → /kpis/meetings[/{id}]
//   /cadence/{x}              → /kpis/{x}   (de/{id}, my-numbers, my-scorecard, priorities,
//                                            publications, entry/…, import, issues, vision)
// UX-A (ux pass 2026-10-27, founder-approved): pages that became tabs redirect from their own
// route files, so the router keeps one entry per path (tests: routes.test.tsx):
//   /kpis/publications         → /kpis/scorecard?view=published   (c.$slug.kpis.publications.tsx)
//   /kpis/my-scorecard         → /kpis/my-numbers?tab=scorecard   (c.$slug.kpis.mine.tsx)
//   /setup/connections/checks  → /setup/connections?tab=checks    (subscriptions-routes.tsx)
// UX-B (the Workforce half of the same pass), from c.$slug.agents.tsx and c.$slug.kpis.tsx:
//   /agents/budgets[/{id}]     → /setup/budgets[/{id}]
//   /agents/skills[/{id}]      → /setup/skill-packs[/{id}]  (managers; a member reads it in place)
//   /agents/wakeups            → /agents/activity?state=wakeups
//   /agents/de/{id}/edit?step= → /agents/de/{id}?tab=<the step's section>
//   /kpis/de/{id}              → /agents/de/{id}?tab=scorecard
import { createRoute, redirect } from "@tanstack/react-router";
import { companyRoute } from "./c.$slug";
import { setupRoute } from "./c.$slug.setup";

type Loc = { search: unknown };
const searchOf = (location: Loc) => location.search as Record<string, string>;

/**
 * Where an old DE profile link goes: the DE's one page (UX pass 1.2), at its scorecard when the
 * link named no tab (it opened the scorecard), else at the tab it named.
 */
export function deProfileTarget(
  slug: string,
  id: string,
  search: Record<string, string>,
): { to: string; params: Record<string, string>; search: Record<string, string> } {
  const { tab, ...rest } = search;
  return {
    to: "/c/$slug/agents/de/$id",
    params: { slug, id },
    search: { ...rest, tab: tab || "scorecard" },
  };
}

export const legacyScorecardRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: "scorecard",
});

const legacyScorecardIndexRoute = createRoute({
  getParentRoute: () => legacyScorecardRoute,
  path: "/",
  beforeLoad: ({ params, location }) => {
    throw redirect({
      to: "/c/$slug/kpis/scorecard",
      params,
      search: searchOf(location),
      replace: true,
    });
  },
});

const legacyPublicationsRoute = createRoute({
  getParentRoute: () => legacyScorecardRoute,
  path: "publications",
  beforeLoad: ({ params, location }) => {
    throw redirect({
      to: "/c/$slug/kpis/publications",
      params,
      search: searchOf(location),
      replace: true,
    });
  },
});

/** The redirect for an old DE profile URL. */
function deRedirect(params: { slug: string; id: string }, location: Loc) {
  const t = deProfileTarget(params.slug, params.id, searchOf(location));
  return redirect({
    to: t.to as never,
    params: t.params as never,
    search: t.search as never,
    replace: true,
  });
}

const legacyScorecardDeRoute = createRoute({
  getParentRoute: () => legacyScorecardRoute,
  path: "de/$id",
  beforeLoad: ({ params, location }) => {
    throw deRedirect(params, location);
  },
});

export const legacyScorecardTree = legacyScorecardRoute.addChildren([
  legacyScorecardIndexRoute,
  legacyPublicationsRoute,
  legacyScorecardDeRoute,
]);

export const legacySetupDesRoute = createRoute({
  getParentRoute: () => setupRoute,
  path: "des",
  beforeLoad: ({ params, location }) => {
    throw redirect({ to: "/c/$slug/agents", params, search: searchOf(location), replace: true });
  },
});

export const legacySetupDeRoute = createRoute({
  getParentRoute: () => setupRoute,
  path: "des/$id",
  beforeLoad: ({ params, location }) => {
    throw deRedirect(params, location);
  },
});

const moved = (path: string, to: string) =>
  createRoute({
    getParentRoute: () => setupRoute,
    path,
    beforeLoad: ({ params, location }) => {
      throw redirect({
        to: to as never,
        params: params as never,
        search: searchOf(location) as never,
        replace: true,
      });
    },
  });

export const legacySetupEntryRoutes = [
  moved("entry/hours", "/c/$slug/kpis/entry/hours"),
  moved("entry/numbers", "/c/$slug/kpis/entry/numbers"),
  moved("import", "/c/$slug/kpis/import"),
] as const;

/**
 * Where an old Chart or Cadence path goes (KPIS-MODE): the path under `/c/{slug}/`, from the old
 * mode and the rest of its path (no leading slash).
 */
export function legacyModeTarget(mode: LegacyMode, rest: string): string {
  const r = rest.replace(/^\/+|\/+$/g, "");
  // NAV-REDESIGN (2026-10-29): Reports moved into KPIs, Screens became the rail's Live.
  if (mode === "reports") return r ? `kpis/reports/${r}` : "kpis/reports";
  if (mode === "screens") return "live";
  // SHELL-DECISIONS (2026-10-30): Home became Desk.
  if (mode === "home") return r ? `desk/${r}` : "desk";
  if (mode === "chart") {
    if (r === "" || r === "editor") return "kpis/chart";
    if (r === "finance" || r.startsWith("finance/")) return `kpis/reports/${r}`;
    return `kpis/chart/${r}`;
  }
  if (r === "") return "kpis/scorecard";
  if (r === "meeting" || r.startsWith("meeting/")) return `kpis/meetings${r.slice(7)}`;
  return `kpis/${r}`;
}

type LegacyMode = "chart" | "cadence" | "reports" | "screens" | "home";

type SplatLoc = { searchStr?: string; hash?: string };

const legacyMode = (mode: LegacyMode) => {
  const root = createRoute({ getParentRoute: () => companyRoute, path: mode });
  const go = (slug: string, rest: string, location: SplatLoc) => {
    const hash = location.hash ? `#${location.hash}` : "";
    return redirect({
      href: `/c/${slug}/${legacyModeTarget(mode, rest)}${location.searchStr ?? ""}${hash}`,
      replace: true,
    });
  };
  const index = createRoute({
    getParentRoute: () => root,
    path: "/",
    beforeLoad: ({ params, location }) => {
      throw go((params as { slug: string }).slug, "", location as SplatLoc);
    },
  });
  const splat = createRoute({
    getParentRoute: () => root,
    path: "$",
    beforeLoad: ({ params, location }) => {
      const p = params as { slug: string; _splat?: string };
      throw go(p.slug, p._splat ?? "", location as SplatLoc);
    },
  });
  return root.addChildren([index, splat]);
};

/** `/chart/…` and `/cadence/…` (the modes before KPIs): every path redirects. */
export const legacyChartTree = legacyMode("chart");
export const legacyCadenceTree = legacyMode("cadence");
/** NAV-REDESIGN: `/reports/…` → `/kpis/reports/…` (report mails, bookmarks), `/screens` → `/live`. */
export const legacyReportsTree = legacyMode("reports");
export const legacyScreensTree = legacyMode("screens");
/** SHELL-DECISIONS (2026-10-30): `/home` → `/desk` (Home was renamed Desk). */
export const legacyHomeTree = legacyMode("home");
